import { Box, Flex, Heading, Stack, StackDivider } from '@chakra-ui/layout';
import {
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text,
} from '@chakra-ui/react';
import { type ReactNode, useContext, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { PagePaths, PageTitles } from '@revelio/core';

import { CircleIcon } from '../../../shared/ui/icons';
import { PageProvider } from '../../CustomDash/components/PageConfigProvider/PageConfigProvider';
import { PageStateProvider } from '../../CustomDash/components/PageState/PageStateProvider';
import { FilterProvider } from '../../CustomDash/filters/contexts/FilterProvider';
import { FilterId } from '../../CustomDash/filters/types/filters';
import { REPORT_TEMPLATES } from '../../CustomDash/templates/report-templates-registry';
import DashboardPage from '../../DashboardPage';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../utils/report-builder-paths';
import {
  CustomReportBuilderContext,
  CustomReportBuilderProvider,
} from './custom-report-builder-provider';
import { useAllReports } from './report-builder.repository';

interface IStep {
  id: string;
  title: string;
  path: ReportBuilderDeliverablePaths;
  description?: string | ReactNode;
}

export enum ReportBuilderDeliverablePaths {
  REPORT_TYPE = 'report_type',
  ENTITY = 'entity',
  CONFIRMATION = 'confirmation',
}

export const steps: IStep[] = [
  {
    id: 'report_type',
    title: 'Report Type',
    path: ReportBuilderDeliverablePaths.REPORT_TYPE,
  },
  {
    id: 'entity',
    title: 'Entity',
    path: ReportBuilderDeliverablePaths.ENTITY,
  },
  {
    id: 'confirmation',
    title: 'Confirmation',
    path: ReportBuilderDeliverablePaths.CONFIRMATION,
  },
];

export const ReportBuilderPage = () => {
  return (
    <CustomReportBuilderProvider>
      <ReportBuilderPageContent />
    </CustomReportBuilderProvider>
  );
};

const ReportBuilderPageContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedReportTemplate } = useContext(CustomReportBuilderContext);
  const reportBuilderDeliverables = useAllReports();

  const currentStepIndex = steps.findIndex((step) =>
    location.pathname.includes(step.path)
  );

  useEffect(() => {
    if (location.search.length > 0) {
      navigate(
        {
          pathname: location.pathname,
          search: '',
        },
        { replace: true }
      );
      return;
    }

    if (location.pathname.endsWith(PagePaths.REPORT_BUILDER)) {
      navigate(getReportBuilderPath(ReportBuilderStep.REPORT_TYPE), {
        replace: true,
      });
      return;
    }

    const deliverablePath = location.pathname.split('/').pop() as string;
    if (
      Object.values(ReportBuilderDeliverablePaths).includes(
        deliverablePath as ReportBuilderDeliverablePaths
      )
    ) {
      const currentStepIndex = steps.findIndex(
        (s) => s.path === deliverablePath
      );

      const requiresReportContext =
        currentStepIndex > ReportBuilderStep.REPORT_TYPE;
      const hasReportContext =
        selectedReportTemplate !== null || reportBuilderDeliverables.length > 0;

      if (requiresReportContext && !hasReportContext) {
        navigate(getReportBuilderPath(ReportBuilderStep.REPORT_TYPE), {
          replace: true,
        });
      }
    }
  }, [
    navigate,
    location.pathname,
    location.search,
    selectedReportTemplate,
    reportBuilderDeliverables.length,
  ]);

  return (
    <DashboardPage title={[PageTitles.REPORT_BUILDER]} loading={false}>
      <Box rounded="lg" h="calc(100vh - 100px)">
        <Stack
          h="100%"
          direction="row"
          divider={<StackDivider width="15px" backgroundColor="gray.100" />}
          spacing="0"
          css={{
            '> div:first-of-type, > div:last-child': {
              background: 'white',
              borderRadius: '0.5rem',
            },
          }}
        >
          <Flex
            width="240px"
            height="100%"
            padding="32px 24px 16px"
            flexDirection="column"
            justifyContent="space-between"
            rounded="lg"
          >
            <Stepper
              colorScheme="green"
              index={currentStepIndex}
              orientation="vertical"
              height="500px"
              maxH="500px"
              gap="0"
              size="md"
            >
              {steps.map((step, index) => (
                <Step key={`dlStep-${index}`}>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<CircleIcon boxSize="12" color="gray.200" />}
                      active={
                        <CircleIcon
                          boxSize="7"
                          color="green.500"
                          data-testid={`deliverable_${step.id}_step_active_icon`}
                        />
                      }
                    />
                  </StepIndicator>

                  <Box>
                    <StepTitle as="span">
                      <Heading
                        as="h1"
                        pt="6px"
                        pl="6px"
                        color="text.primary"
                        fontWeight="600"
                        fontSize="md"
                      >
                        {step.title}
                      </Heading>
                    </StepTitle>
                    {step.description &&
                    typeof step.description === 'string' ? (
                      <Box pl="6px">
                        <StepDescription>{step.description}</StepDescription>
                      </Box>
                    ) : (
                      <Text as="span" fontSize="2xs" color="text.primary">
                        {step.description}
                      </Text>
                    )}
                  </Box>

                  <StepSeparator />
                </Step>
              ))}
            </Stepper>
          </Flex>
          <ReportBuilderPageOutlet />
        </Stack>
      </Box>
    </DashboardPage>
  );
};

const ReportBuilderPageOutlet = () => {
  const { selectedReportTemplate } = useContext(CustomReportBuilderContext);
  const pageConfig =
    selectedReportTemplate ?? REPORT_TEMPLATES['report-company-overview'];
  const firstTab = pageConfig.tabs?.[0];

  return (
    <FilterProvider
      key={pageConfig.id}
      pageId={pageConfig.id}
      revelioPageId={pageConfig.revelioPageId}
      primaryFilterId={firstTab?.tabContent?.primaryFilter?.id}
      primaryFilterSelectionLimit={
        firstTab?.tabContent?.primaryFilter?.selectionLimit
      }
      primaryFilterSelectionDefault={
        firstTab?.tabContent?.primaryFilter?.selectionDefault
      }
      defaultSelectionOverrides={{
        [FilterId.COMPANY]: [],
        [FilterId.GEOGRAPHY]: [],
        [FilterId.ROLE]: [],
      }}
      persistScope={false}
    >
      <PageStateProvider
        initialPageState={{
          metricOverrides: {},
          selections: {},
          primaryEntities: {},
          multiPrimaryEntities: {},
          dimensionSourceOverrides: {},
          initialSelections: {},
          activeTabId: firstTab?.id ?? 'tab-1',
        }}
      >
        <PageProvider initialConfig={pageConfig}>
          {() => <Outlet />}
        </PageProvider>
      </PageStateProvider>
    </FilterProvider>
  );
};
